import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import api from "../../axios/api";
import { showNotification } from "../../redux/slices/notificationSlice";
import { useForm } from "react-hook-form";
import CustomButton from "../../componentsII/Buttons";
import ConfirmationModal from "../../componentsII/ConfirmationModal";
import CustomCard from "../../componentsII/Card";
import { CompInput, CompSelect } from "../../componentsII/FormComponents";

const HeatSteamUsageDetailModal = ({
  detailModalData: initialData,
  refetch,
  handleCloseMainDetailModal,
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state);

  const [detailModalData, setDetailModalData] = useState({ ...initialData });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [fuels, setFuels] = useState([]);
  const [fuelUnit, setFuelUnit] = useState([]);

  const TypeOptions = [{value:'ONSITE_HEATSTEAM',label:'Onsite heat and steam'},{value:'DISTRICT_HEATSTEAM',label:'District heat and steam'}];

  const schema = Yup.object().shape({
    heatsteem_type: Yup.string().required("Type is required"),
    qty_unit_id: Yup.number().required("Unit is required").test('not-zero', 'Unit is required', value => value !== 0),
    heatsteem_qty: Yup.number().required("Fuel quantity is required"),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: detailModalData?.id,
      comp_location_id: detailModalData?.value ,
      heatsteem_type: detailModalData?.heatsteem_consumption_type || 0,
      qty_unit_id: detailModalData?.qty?.qty_unit_id || 0,
      heatsteem_qty: detailModalData?.heatsteem_qty || null,
    },
  });

  const getFuelUnit = async () => {
    try {
      const res = await api.get(`/common/measurementType?type_code=ELECTRICITY`);
      setFuelUnit(res.data.data);
    } catch (error) {
      console.error("Error fetching models:", error);
    }
  };

  useEffect(() => {
    getFuelUnit();
  }, []);

  const handleDelete = async () => {
    try {
      const response = await api.delete(
        `/consumption/deleteHeatSteamUsage/${detailModalData?.id}`
      );
      if (response.data) {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "success",
          })
        );
      } else {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "error",
          })
        );
        console.log(response.data.message);
      }
      refetch();
      handleCloseMainDetailModal();
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsDeleteModalOpen(false);
  };

  const handleEditSubmit = async (data) => {
    const body = { ...data };
    try {
      const response = await api.post(
        "/consumption/addUpdateHeatSteamUsage",
        body
      );
      if (response.data.status === "200") {
        dispatch(
          showNotification({ message: response.data.message, type: "success" })
        );
        refetch();
        handleCloseMainDetailModal();
      } else {
        dispatch(
          showNotification({ message: response.data.message, type: "error" })
        );
        setIsEditable(false);
        console.log(response.data.message);
      }
      console.log("Form submitted successfully:", response);
    } catch (error) {
      dispatch(
        showNotification({
          message: error.response.data.message,
          type: "error",
        })
      );
      console.error("Error submitting form:", error);
    }
  };

  return (
    <div>
      <div>
        <div className="flex border-b border-dashed border-gray-500 ">
          <div className="px-8 w-full text-center">
            <h1 className="text-2xl font-bold text-green-600">
              {detailModalData?.id ? "Edit" : "Add"}{" "}
              {user?.selectedYear?.start_year}{" "}
              heat & steam data usage
            </h1>
            <p className="text-gray-500 mb-5 mt-1 text-md font-medium px-10">
              To calculate emission for this equipment, we'll gather the amount
              of fuel this equipment used during{" "}
              {user?.selectedYear?.start_year}
            </p>
          </div>
        </div>

        <div className="px-6">
          <div className="flex justify-center w-full items-center py-6">
            <div className="w-[45%] rounded-[20px] border-[10px] shadow-md">
              <CustomCard
                image={detailModalData.img}
                title={detailModalData?.loc_name}
                subtitleI={`${detailModalData?.city_name}`}
                subtitleII={detailModalData?.gross_area}
                subtitleIII={detailModalData?.zipcode}
              />
            </div>
          </div>
          <form onSubmit={handleSubmit(handleEditSubmit)}>
          <div className="grid grid-cols-1 gap-4 py-3">
          <CompSelect
                title={`Select Type of Heat & Steam for ${detailModalData.loc_name}`}
                name="heatsteem_type"
                isError={!!errors.heatsteem_type}
                errorText={errors.heatsteem_type?.message}
                control={control}
                options={TypeOptions}
              />
              </div>
            <div className="grid grid-cols-2 gap-4">
              <CompInput
                type="number"
                title={`How much heat & steam was purchased for ${detailModalData.loc_name} ?`}
                name="heatsteem_qty"
                isError={!!errors.heatsteem_qty}
                errorText={errors.heatsteem_qty?.message}
                control={control}
              />
              <CompSelect
                title="Unit of heat & steam"
                name="qty_unit_id"
                isError={!!errors.qty_unit_id}
                errorText={errors.qty_unit_id?.message}
                control={control}
                options={fuelUnit}
              />
            </div>
            <div className="flex justify-between items-center w-full py-2">
              {detailModalData?.id ? (
                <div>
                  <CustomButton
                    className="bg-red-500 hover:bg-red-700 text-white "
                    click={() => setIsDeleteModalOpen(true)}
                    text="Delete"
                  />
                </div>
              ) : (
                <div></div>
              )}

              <div className="w-full flex justify-end gap-5 items-center py-5">
                <CustomButton
                  className="bg-gray-500 hover:bg-gray-700 text-white "
                  click={() => handleCloseMainDetailModal(false)}
                  text="Cancel"
                />
                <CustomButton
                  className="bg-green-600 hover:bg-green-700 text-white "
                  type="submit"
                  text="Submit"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
      <ConfirmationModal
        isModalOpen={isDeleteModalOpen}
        cancelText="Cancel"
        confirmText="Delete"
        onConfirmClick={handleDelete}
        onCancelClick={() => setIsDeleteModalOpen(false)}
        text={
          <>
            <p>Are you sure you want to delete this item?</p>
            <p>This action cannot be undone.</p>
          </>
        }
        header={<div className="text-red-500">Delete Vehicle Usage</div>}
      />
    </div>
  );
};

export default HeatSteamUsageDetailModal;


